import Workspace from 'iam/models/Workspace'
import { GetCurrentMember, GetCurrentUser } from '../../../iam/SessionStore'
import WorkspaceMembershipStore, {
  GetCurrentUserWorkspaceMemberships,
} from '../../../iam/WorkspaceMembershipStore'
import FirmDashboardJobsViewModel from './FirmDashboardJobsViewer/FirmDashboardJobTableViewModel'
import { Storage, localStorage as appStorage } from 'infra/storage'
import UserDataStore from 'iam/UserDataStore'
import { AlertFactory } from 'alerts/Alert'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamStore from 'iam/TeamStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { JobTicketStore } from 'jobs/tickets/JobTicketStore'
import { FilterStorage } from 'infra/FiltersViewModel'
import JobRootViewModel from 'jobs/views/JobRoot/JobRootViewModel'
import FirmDashboardWorkspaceSearchViewModel from './FirmDashboardViewHeading/FirmDashboardWorkspaceSearchViewModel'
import FirmDashboardQuickSelectFilterViewModel from './FirmDashboardQuickSelectFilter/FirmDashboardQuickSelectFilterViewModel'
import Member from 'iam/models/Member'
import FirmDashboardStore from './FirmDashboardStore'
import OverviewSidebarViewModel from './FirmDashboardOverviewSidebar/FirmDashboardOverviewSidebarViewModel'
import once from 'lodash/once'
import { task } from 'mobx-task'
import { action, computed, makeObservable } from 'mobx'
import { Observable } from 'rxjs'
import { JobStatsAPI } from '../../job-stats/API'
import { SearchInventoryItems } from 'admin/InventoryStore'
import { LastWorkspaceStorage } from 'infra/LastWorkspace'

/**
 * View model for managing the firm dashboard page.
 */
export default class FirmDashboardViewModel {
  /**
   * Storage for the last selected workspace.
   */
  private lastWorkspaceStorage = new LastWorkspaceStorage(
    this.localStorage,
    'firmDashboard',
  )

  private readonly globalSnapshotKey = 'globalWorkspaceSnapshot.firmDashboard'

  /**
   * Firm dashboard store.
   */
  firmDashboardStore = new FirmDashboardStore(this.jobStatsAPI)

  /**
   * Firm dashboard job's table view model.
   */
  firmDashboardJobTableViewModel = new FirmDashboardJobsViewModel(
    this.jobFetcher,
    this.newAlert,
    (params) => this.publishedLegendStore.findLegends(params),
    this.workspaceMembershipsStore.getCurrentUserWorkspaceMemberships,
    this.teamMemberStore.fetchUserTeamMembers,
    this.getCurrentMember,
    this.jobTicketStore,
    (ns) => new FilterStorage(this.localStorage, ns),
    this.userDataStore,
    this.teamMemberStore.filterTeamMembers.bind(this.teamMemberStore),
    () => this.jobRootViewModel.canEditDraftJobAsAdmin,
    (params) => this.teamStore.getUserTeams(params),
    () => this.jobRootViewModel.useEditDraftJobForFirmAdmin,
    this.searchServiceItems,
  )

  /**
   * Firm dashboard workspace selector view model.
   */
  workspaceSelectorViewModel = new FirmDashboardWorkspaceSearchViewModel(
    this.getCurrentUserWorkspaceMemberships,
    this.teamMemberStore,
  )

  /**
   * Firm dashboard quick select filters view model.
   */
  quickSelectFilterViewModel = new FirmDashboardQuickSelectFilterViewModel(
    this.jobFetcher,
  )

  /**
   * Firm dashboard overview sidebar view model.
   */
  overviewSidebarViewModel = new OverviewSidebarViewModel(
    this.jobFetcher,
    this.jobTicketStore,
    this.newAlert,
    (params) => this.publishedLegendStore.findLegends(params),
    this.workspaceMembershipsStore.getCurrentUserWorkspaceMemberships,
    this.getCurrentMember,
    this.userDataStore,
    (ns) => new FilterStorage(this.localStorage, ns),
    this.teamMemberStore.filterTeamMembers.bind(this.teamMemberStore),
    (params) => this.teamStore.getUserTeams(params),
    this.firmDashboardStore,
    this.getCurrentUser,
    this.searchServiceItems,
  )

  /**
   * Ensure we only activate the workspace searcher once.
   */
  private activateWorkspaceSearcher = once(() =>
    this.workspaceSelectorViewModel.activate(),
  )

  /**
   * Ensure we only activate the quick select filters once.
   */
  private activateQuickSelectFilter = once(() =>
    this.quickSelectFilterViewModel.activate(),
  )

  /**
   * Constructor
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private getCurrentMember: GetCurrentMember,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private currentMember$: Observable<Member>,
    private localStorage: Storage,
    private userDataStore: UserDataStore,
    private newAlert: AlertFactory,
    private teamMemberStore: TeamMemberStore,
    private workspaceMembershipsStore: WorkspaceMembershipStore,
    private teamStore: TeamStore,
    private publishedLegendStore: PublishedLegendStore,
    private jobTicketStore: JobTicketStore,
    private jobRootViewModel: JobRootViewModel,
    private jobStatsAPI: JobStatsAPI,
    private readonly getCurrentUser: GetCurrentUser,
    private searchServiceItems: SearchInventoryItems,
  ) {
    makeObservable(this)

    this.currentMember$.subscribe((member) => {
      const userId = member.user.publicId
      if (!userId) {
        return
      }

      // Use global workspace selection if available in dashboard workspaces
      const globalWorkspace =
        this.workspaceSelectorViewModel.workspacesWhereUserCanViewDashboard.find(
          (ws) => ws.id === member.workspace.id,
        )
      if (globalWorkspace) {
        this.selectWorkspaceForFiltering(globalWorkspace)
        return
      }

      // Fall back to saved workspace
      const savedWorkspaceId = this.lastWorkspaceStorage.load(userId)
      if (savedWorkspaceId) {
        const savedWorkspace =
          this.workspaceSelectorViewModel.workspacesWhereUserCanViewDashboard.find(
            (ws) => ws.id === savedWorkspaceId,
          )
        if (savedWorkspace) {
          this.selectWorkspaceForFiltering(savedWorkspace)
          return
        }
      }

      // Fall back to first available workspace
      if (
        this.workspaceSelectorViewModel.workspacesWhereUserCanViewDashboard
          .length
      ) {
        this.selectWorkspaceForFiltering(
          this.workspaceSelectorViewModel
            .workspacesWhereUserCanViewDashboard[0],
        )
      }
    })
  }

  /**
   * All Workspaces where the user have the permission to view the dashboard.
   */
  @computed
  get filteredWorkspacesWhereUserCanViewDashboard() {
    return this.workspaceSelectorViewModel.filteredWorkspaces
  }

  /**
   * Whether to display the dropdown arrow
   * when user can view dashboard in more than 1 workspace.
   */
  @computed
  get workspaceSelectorUseDropdown() {
    return (
      this.workspaceSelectorViewModel.workspacesWhereUserCanViewDashboard
        .length > 1
    )
  }

  /**
   * Whether the current user can view the firm dashboard
   * based on the permission and the feature toggle.
   */
  @computed
  get canViewFirmDashboard() {
    return this.getCurrentUserWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('VIEW_FIRM_DASHBOARD'),
    )
  }

  /**
   * Selects the workspace to filter by in the firm dashboard.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceSelectorViewModel.workspace = workspace
    this.firmDashboardJobTableViewModel.setWorkspace(workspace)
    this.overviewSidebarViewModel.setWorkspace(workspace)
    this.quickSelectFilterViewModel.setWorkspace(workspace)

    // Save the selected workspace
    const userId = this.getCurrentMember().user.publicId
    if (workspace.id && userId) {
      this.lastWorkspaceStorage.save(workspace.id, userId)
      // Snapshot the current global workspace so we can detect changes on next visit
      const globalId = this.getCurrentMember().workspace.id
      if (globalId) {
        appStorage.setItem(this.globalSnapshotKey, globalId)
      }
    }
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    this.activateWorkspaceSearcher()
    this.activateQuickSelectFilter()
  })
}
